import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { cn } from '@/shared/utils/cn';

import { HelpContent } from './HelpBox';
import { HelpIcon, XIcon } from './icons';

/**
 * A round `?` that opens the help of a screen in a panel next to it.
 *
 * The explanation used to be a full-width box above the content on every screen; the
 * first email of the inbox started at 73 % of the height of a 1440x900 window. Now the
 * help is a click away and the content is first. The panel closes with Escape, with a
 * click outside, or with its own close button, and gives the focus back to the `?` so a
 * keyboard reader does not land at the top of the page.
 *
 * Not a `<details>`: `<summary>` has no ARIA role that every reader agrees on, and the
 * panel has to close on a click outside anyway, which `<details>` does not do.
 *
 * `content` is what `PageHeader` receives as `help`: a list of nodes becomes bullets (the
 * same shape `HelpBox` takes), anything else is rendered as it is.
 */
export function HelpPopover({
  content,
  label = 'Ayuda de esta pantalla',
  className,
}: {
  content: ReactNode;
  /** The accessible name of the button and the title of the panel. */
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    function onPointerDown(event: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  function close() {
    setOpen(false);
    buttonRef.current?.focus();
  }

  return (
    <div ref={rootRef} className={cn('inline-flex', className)}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'inline-flex size-6 shrink-0 items-center justify-center rounded-full ring-1 ring-inset transition-colors',
          'focus-visible:outline-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
          open
            ? 'bg-accent text-white ring-accent'
            : 'text-ink-3 ring-line hover:text-accent hover:ring-accent/40 bg-card',
        )}
      >
        <HelpIcon size={15} />
      </button>
      {open ? (
        <div
          id={panelId}
          role="region"
          aria-label={label}
          className={cn(
            'border-line bg-card text-ink-2 absolute top-full right-0 left-0 z-20 mt-2 rounded-lg border p-4 text-[13.5px] leading-relaxed shadow-lg',
            'sm:right-auto sm:w-[26rem]',
          )}
        >
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="text-ink text-[14px] font-semibold">{label}</p>
            <button
              type="button"
              aria-label="Cerrar la ayuda"
              onClick={close}
              className="text-ink-3 hover:bg-paper-2 hover:text-ink inline-flex size-7 items-center justify-center rounded-md"
            >
              <XIcon size={16} />
            </button>
          </div>
          {Array.isArray(content) ? <HelpContent items={content} /> : content}
        </div>
      ) : null}
    </div>
  );
}
